import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { CreateOrderNoteRequest } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API function to create a new order note
 * @param servicesProviderId - Required services provider ID
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 * @param noteData - The note data to create
 */
export const createOrderNote = async (
	servicesProviderId: string,
	orderId: string,
	noteData: CreateOrderNoteRequest,
): Promise<void> => {
	await apiClient.post(
		ordersEndpoints.createTimelineNote(servicesProviderId, orderId),
		noteData,
	);
};

/**
 * Hook for creating a new order note
 * Includes cache invalidation for the timeline query
 */
export const useCreateOrderNote = (orderId: string | undefined) => {
	const servicesProviderId = useCurrentServicesProviderId();
	const queryClient = useQueryClient();

	return useMutation<void, Error, CreateOrderNoteRequest>({
		mutationFn: async (noteData) => {
			if (!orderId || !servicesProviderId) {
				throw new Error(
					'Unable to create note without an order ID or services provider ID.',
				);
			}

			return createOrderNote(servicesProviderId, orderId, noteData);
		},
		onSuccess: () => {
			if (!orderId || !servicesProviderId) return;

			// Invalidate entire order (cascades to timeline and all other tabs)
			queryClient.invalidateQueries({
				queryKey: queries.orders.detail(servicesProviderId, orderId).queryKey,
			});
		},
	});
};
